import * as Element from "../element";
import {
    Docx as DocxStructure,
    generateDefaultDocxStructure,
    listBullets,
    listNumbering,
    numNumberingXml,
} from "../structure";
import {
    Zip,
    applyHtmlSpecialChars,
    generateRandomNumber,
    generateRegExpForReplacement,
    generateUniqid,
} from "../util/";
import { WordFragment } from "./wordfragment";

/**
 * Create DOCX
 */
class CreateDocx {
    /**
     * phpdocx version
     */
    static readonly PHPDOCX_VERSION = "15.0";

    /**
     * Bidi
     */
    protected bidi: boolean;

    /**
     * Custom list styles. Keep relationship between list style names and internal number ids
     */
    protected customListStyles: Map<string, number>;

    /**
     * DOCX contents
     */
    protected docxContent: DocxStructure;

    /**
     * External defaults
     */
    protected externalDefaults: Map<string, string>;

    /**
     * External relationships
     */
    protected externalRelationships: Element.Relationship[];

    /**
     * Bookmark Ids
     */
    protected idsBookmarks: number[];

    /**
     * DocPr Ids
     */
    protected idsDocPr: number[];

    /**
     * Numbering Ids
     */
    protected idsNumbering: number[];

    /**
     * Is WordFragment
     */
    protected isWordFragment: boolean;

    /**
     * Preprocessed template
     */
    protected preprocessed: boolean;

    /**
     * Regular expression to get variables
     */
    protected regExprVariableSymbols: string;

    /**
     * RTL
     */
    protected rtl: boolean;

    /**
     * Template symbol start
     */
    protected templateSymbolStart: string;

    /**
     * Template symbol end
     */
    protected templateSymbolEnd: string;

    /**
     * XML content
     */
    protected xml: string;

    /**
     * Constructor
     *
     * @param docxStructure - Default DOCX structure
     */
    constructor(docxStructure?: DocxStructure) {
        this.bidi = false;
        this.customListStyles = new Map<string, number>();
        if (docxStructure != null) {
            this.docxContent = docxStructure;
        } else {
            this.docxContent = generateDefaultDocxStructure(); // default base template
            this.docxContent.initDocxStructure();
        }
        this.externalDefaults = new Map<string, string>();
        this.externalRelationships = [];
        this.idsBookmarks = [];
        this.idsNumbering = [];
        this.idsDocPr = [];
        this.isWordFragment = false;
        this.preprocessed = false;
        this.regExprVariableSymbols = "(.*?)";
        this.rtl = false;
        this.templateSymbolStart = "$";
        this.templateSymbolEnd = "$";
        this.xml = "";
    }

    /**
     * Getter docx structure
     *
     * @returns DOCX structure
     */
    public getDocxContent(): DocxStructure {
        return this.docxContent;
    }

    /**
     * Getter regExprVariableSymbols
     *
     * @returns Regular expression variable symbols
     */
    public getRegExprVariableSymbols(): string {
        return this.regExprVariableSymbols;
    }

    /**
     * Getter external defaults
     *
     * @returns external relationships
     */
    public getExternalDefaults(): Map<string, string> {
        return this.externalDefaults;
    }

    /**
     * Getter external relationships
     *
     * @returns external relationships
     */
    public getExternalRelationships(): Element.Relationship[] {
        return this.externalRelationships;
    }

    /**
     * Getter preprocessed
     *
     * @returns Preprocessed
     */
    public getPreprocessed(): boolean {
        return this.preprocessed;
    }

    /**
     * Setter preprocessed
     *
     * @param preprocessed - Preprocessed
     * @returns this
     */
    public setPreprocessed(preprocessed: boolean): this {
        this.preprocessed = preprocessed;

        return this;
    }

    /**
     * Setter regExprVariableSymbols
     *
     * @param regExprVariableSymbols - Regular expression variable symbols
     * @returns this
     */
    public setRegExprVariableSymbols(regExprVariableSymbols: string): this {
        this.regExprVariableSymbols = regExprVariableSymbols;

        return this;
    }

    /**
     * Adds bookmark
     *
     * @param name - Bookmark name
     * @param options - Bookmark options
     * @returns this
     */
    public addBookmark(
        name: string,
        options: Element.OptionsBookmark = {}
    ): this {
        // new bookmark Id
        const bookmarkId = generateRandomNumber(
            99999999,
            999999999,
            this.idsBookmarks
        );
        this.idsBookmarks.push(bookmarkId);

        const bookmarkElement = new Element.CreateBookmark(
            options,
            bookmarkId,
            name
        );
        const bookmarkContent = bookmarkElement.create();

        if (this.isWordFragment) {
            this.xml += bookmarkContent;
        } else {
            this.docxContent.addXmlContent(bookmarkContent);
        }

        return this;
    }

    /**
     * Adds break
     *
     * @param type - Break type
     * @param options - Break options
     * @returns this
     */
    public addBreak(
        type: Element.BreakType = "line",
        options: Element.OptionsBreak = {}
    ): this {
        const breakElement = new Element.CreateBreak(options, type);
        let breakContent = breakElement.create();

        // wrap in a paragraph
        breakContent = this.generateParagraph(breakContent);

        if (this.isWordFragment) {
            this.xml += breakContent;
        } else {
            this.docxContent.addXmlContent(breakContent);
        }

        return this;
    }

    /**
     * Adds date and time
     *
     * @param options - Date and time options
     * @param optionsParagraph - Paragraph options
     * @returns this
     */
    public addDateTime(
        options: Element.OptionsDateTime = {},
        optionsParagraph: Element.OptionsParagraph = {}
    ): this {
        // handle RTL class settings
        optionsParagraph = this.setRtlValues(optionsParagraph);

        const dateTimeElement = new Element.CreateDateTime(
            options,
            optionsParagraph
        );
        let dateTimeContent = dateTimeElement.create();

        // wrap in a paragraph
        dateTimeContent = this.generateParagraph(
            dateTimeContent,
            optionsParagraph
        );

        if (this.isWordFragment) {
            this.xml += dateTimeContent;
        } else {
            this.docxContent.addXmlContent(dateTimeContent);
        }

        return this;
    }

    /**
     * Adds external file
     *
     * @param options - Altchunk options
     * @returns this
     */
    public addExternalFile(options: Element.OptionsAltchunk): this {
        // new altchunk id
        const altchunkId = generateUniqid(options.type);

        const altchunkElement = new Element.CreateAltchunk(options, altchunkId);
        const altchunkContent = altchunkElement.create();

        if (this.isWordFragment) {
            this.xml += altchunkContent + "<w:p/>";
        } else {
            this.docxContent.addXmlContent(altchunkContent);
        }

        // keep external dependencies
        this.insertExternalDependencies(altchunkElement);

        return this;
    }

    /**
     * Adds footer
     *
     * @param footers - Footers
     * @returns this
     */
    public addFooter(footers: Element.OptionsFooter[]) {
        footers.forEach(footer => {
            const footerElement = new Element.CreateReference(footer, "footer");
            this.docxContent.addXmlContentReference(footerElement);

            if (footer.type == "even") {
                this.docxSettings({ evenAndOddHeaders: true });
            }

            // keep external dependencies
            this.insertExternalDependencies(footer.text, "footer");
        });

        return this;
    }

    /**
     * Adds header
     *
     * @param headers - Headers
     * @returns this
     */
    public addHeader(headers: Element.OptionsHeader[]) {
        headers.forEach(header => {
            const headerElement = new Element.CreateReference(header, "header");
            this.docxContent.addXmlContentReference(headerElement);

            if (header.type == "even") {
                this.docxSettings({ evenAndOddHeaders: true });
            }

            // keep external dependencies
            this.insertExternalDependencies(header.text, "header");
        });

        return this;
    }

    /**
     * Adds image
     *
     * @param options - Image options
     * @param optionsParagraph - Paragraph options
     * @returns this
     */
    public addImage(
        options: Element.OptionsImage,
        optionsParagraph: Element.OptionsParagraph = {}
    ): this {
        // handle RTL class settings
        optionsParagraph = this.setRtlValues(optionsParagraph);

        // new image id
        const imageId = generateUniqid("image");
        let imageLinkId = undefined;
        if (options.link != undefined) {
            imageLinkId = generateUniqid("image");
        }

        // new docPr Id
        const docPrId = generateRandomNumber(
            99999999,
            999999999,
            this.idsDocPr
        );
        this.idsDocPr.push(docPrId);

        const imageElement = new Element.CreateImage(
            options,
            imageId,
            docPrId,
            imageLinkId
        );
        let imageContent = imageElement.create();

        // wrap in a paragraph
        imageContent = this.generateParagraph(imageContent, optionsParagraph);

        if (this.isWordFragment) {
            this.xml += imageContent;
        } else {
            this.docxContent.addXmlContent(imageContent);
        }

        // keep external dependencies
        this.insertExternalDependencies(imageElement);

        return this;
    }

    /**
     * Adds link
     *
     * @param options - Link options
     * @param optionsParagraph - Paragraph options
     * @returns this
     */
    public addLink(
        options: Element.OptionsLink,
        optionsParagraph: Element.OptionsParagraph = {}
    ): this {
        // new link id
        const hyperlinkId = generateUniqid("rId");

        // handle RTL class settings
        optionsParagraph = this.setRtlValues(optionsParagraph);

        const linkElement = new Element.CreateLink(
            options,
            optionsParagraph,
            hyperlinkId
        );
        let linkContent = linkElement.create();

        // wrap in a paragraph
        linkContent = this.generateParagraph(linkContent, optionsParagraph);

        if (this.isWordFragment) {
            this.xml += linkContent;
        } else {
            this.docxContent.addXmlContent(linkContent);
        }

        // keep external dependencies
        this.insertExternalDependencies(linkElement);

        return this;
    }

    /**
     * Adds list
     *
     * @param items - List items
     * @param options - List options
     * @returns this
     */
    public addList(
        items: Element.ListNested[],
        options: Element.OptionsList = {}
    ): this {
        let numId;
        if (options.type != null && this.customListStyles.has(options.type)) {
            // custom list style
            numId = this.customListStyles.get(options.type);
            options.numId = numId;
        } else {
            // new list Id
            numId = generateRandomNumber(999, 32000, this.idsNumbering);
            options.numId = numId;
            this.idsNumbering.push(numId);

            // if a custom list style is set but the list style doesn't exist, set the default value
            if (
                options.type != null &&
                options.type != "numbering" &&
                !this.customListStyles.has(options.type)
            ) {
                options.type = "bullets";
            }
        }
        const listElement = new Element.CreateList(options, items);
        const listContent = listElement.create();
        const listOptions = listElement.getOptions();

        // add a preset numbering if the type if bullets or numbering
        if (listOptions.type == "bullets") {
            this.docxContent.addXmlNumbering(
                listBullets.replace(
                    'w:abstractNumId=""',
                    'w:abstractNumId="' + numId + '"'
                ),
                numNumberingXml
                    .replace('w:numId=""', 'w:numId="' + numId + '"')
                    .replace('w:val=""', 'w:val="' + numId + '"')
            );
        } else if (listOptions.type == "numbering") {
            this.docxContent.addXmlNumbering(
                listNumbering.replace(
                    'w:abstractNumId=""',
                    'w:abstractNumId="' + numId + '"'
                ),
                numNumberingXml
                    .replace('w:numId=""', 'w:numId="' + numId + '"')
                    .replace('w:val=""', 'w:val="' + numId + '"')
            );
        }

        if (this.isWordFragment) {
            this.xml += listContent;
        } else {
            this.docxContent.addXmlContent(listContent);
        }

        // keep external dependencies
        this.insertExternalDependencies(listElement);

        return this;
    }

    /**
     * Adds page number
     *
     * @param options - Page number options
     * @param optionsParagraph - Paragraph options
     * @returns this
     */
    public addPageNumber(
        options: Element.OptionsPageNumber = {},
        optionsParagraph: Element.OptionsParagraph = {}
    ): this {
        // handle RTL class settings
        options = this.setRtlValues(options);

        const pageNumberElement = new Element.CreatePageNumber(
            options,
            optionsParagraph
        );
        const pageNumberContent = pageNumberElement.create();

        if (this.isWordFragment) {
            this.xml += pageNumberContent;
        } else {
            this.docxContent.addXmlContent(pageNumberContent);
        }

        return this;
    }

    /**
     * Adds properties
     *
     * @param options - Property options
     * @returns this
     */
    public addProperties(options: Element.OptionsProperties): this {
        this.docxContent.addXmlProperties(options);

        return this;
    }

    /**
     * Adds section
     *
     * @param options - Section options
     * @returns this
     */
    public addSection(options: Element.OptionsSection = {}): this {
        const contents = this.docxContent.getContentPathsByType("document");

        if (contents != null) {
            const targetDocumentContent = this.docxContent.getTextContent(
                contents[0]
            );
            if (targetDocumentContent != null) {
                const sectionContents = this.docxContent.getXmlContents(
                    targetDocumentContent,
                    "(//w:sectPr)[last()]"
                );

                // add the new section from the existing section
                const sectionElement = new Element.CreateSection(options);
                sectionElement.setSectionInit(sectionContents);
                const sectionContent = sectionElement.create();
                this.docxContent.addXmlContent(sectionContent);

                // modify the last section applying the new options
                options = sectionElement.getOptions();
                const optionsSection = options as Element.OptionsSectionLayout;
                optionsSection.onlyLastSection = true;
                this.docxContent.modifySections(optionsSection);
            }
        }

        return this;
    }

    /**
     * Adds table
     *
     * @param items - Table items
     * @param optionsRow - Table row options
     * @param optionsTable - Table options
     * @returns this
     */
    public addTable(
        items: (string | WordFragment | Element.OptionsCellTable)[][],
        optionsRow: Element.OptionsRowTable[] = [],
        optionsTable: Element.OptionsTable = {}
    ): this {
        const tableElement = new Element.CreateTable(
            items,
            optionsRow,
            optionsTable
        );
        const tableContent = tableElement.create();

        if (this.isWordFragment) {
            this.xml += tableContent;
        } else {
            this.docxContent.addXmlContent(tableContent);
        }

        // keep external dependencies
        this.insertExternalDependencies(tableElement);

        return this;
    }

    /**
     * Adds table of contents
     *
     * @param options - Table of contents options
     * @returns this
     */
    public addTableOfContents(
        options: Element.OptionsTableOfContents = {}
    ): this {
        const tocElement = new Element.CreateTableOfContents(options);
        const tocContent = tocElement.create();

        // auto update fields
        if (options.autoUpdate != null && options.autoUpdate) {
            this.docxSettings({ updateFields: true });
        }

        if (this.isWordFragment) {
            this.xml += tocContent;
        } else {
            this.docxContent.addXmlContent(tocContent);
        }

        return this;
    }

    /**
     * Adds text
     *
     * @param options - Text contents and options
     * @param optionsParagraph - Paragraph options
     * @returns this
     */
    public addText(
        options: string | (Element.OptionsText | WordFragment)[],
        optionsParagraph: Element.OptionsParagraph = {}
    ): this {
        // handle RTL class settings
        optionsParagraph = this.setRtlValues(optionsParagraph);

        let textContent = "";
        if (typeof options == "string") {
            const optionsText: Element.OptionsText = {
                text: options,
            };
            options = [optionsText];
        }
        options.forEach(optionsText => {
            const textElement = new Element.CreateText(
                optionsText,
                optionsParagraph
            );
            textContent += textElement.create();

            // keep external dependencies
            this.insertExternalDependencies(textElement);
        });

        // wrap in a paragraph
        textContent = this.generateParagraph(textContent, optionsParagraph);

        if (this.isWordFragment) {
            this.xml += textContent;
        } else {
            this.docxContent.addXmlContent(textContent);
        }

        return this;
    }

    /**
     * Adds WordFragment
     *
     * @param wordFragment - WordFragment
     * @returns this
     */
    public addWordFragment(wordFragment: WordFragment): this {
        if (this.isWordFragment) {
            this.xml += wordFragment.toString();
        } else {
            this.docxContent.addXmlContent(wordFragment.toString());
        }

        // keep external dependencies
        this.insertExternalDependencies(wordFragment);

        return this;
    }

    /**
     * Adds raw WordML contents
     *
     * @param wordMl - WordML
     * @returns this
     */
    public addWordMl(wordMl: string): this {
        if (this.isWordFragment) {
            this.xml += wordMl;
        } else {
            this.docxContent.addXmlContent(wordMl);
        }

        return this;
    }

    /**
     * Creates a character style
     *
     * @param name - Style name
     * @param styles - Character styles
     * @returns
     */
    public createCharacterStyle(
        name: string,
        styles: Element.OptionsRpr
    ): this {
        const rPrElement = new Element.CreateRpr(styles);
        const rPrStyles = rPrElement.create();

        this.docxContent.addXmlStyleCustom(rPrStyles, "character", name);

        return this;
    }

    /**
     * Creates a list style
     *
     * @param name - Style name
     * @param styles - Character styles
     * @returns
     */
    public createListStyle(name: string, styles: Element.OptionsNumPr[]): this {
        // new list Id
        const numId = generateRandomNumber(999, 32000, this.idsNumbering);
        this.idsNumbering.push(numId);
        this.customListStyles.set(name, numId);

        const numPrElement = new Element.CreateNumpr(styles);
        const numPrContent = numPrElement.create();

        // add the new numbering
        this.docxContent.addXmlNumbering(
            numPrContent.replace(
                'w:abstractNumId=""',
                'w:abstractNumId="' + numId + '"'
            ),
            numNumberingXml
                .replace('w:numId=""', 'w:numId="' + numId + '"')
                .replace('w:val=""', 'w:val="' + numId + '"')
        );

        return this;
    }

    /**
     * Creates a paragraph style
     *
     * @param name - Style name
     * @param styles - Paragarph styles
     * @returns
     */
    public createParagraphStyle(
        name: string,
        styles: Element.OptionsParagraph
    ): this {
        const pPrElement = new Element.CreateParagraph(styles);
        const pPrStyles = pPrElement.createPpr();

        this.docxContent.addXmlStyleCustom(pPrStyles, "paragraph", name);

        return this;
    }

    /**
     * Sets DOCX settings
     *
     * @param settings - DOCX settings
     * @returns this
     */
    public docxSettings(settings: Element.OptionsSettings): this {
        const settingsElements = new Element.CreateSettings(settings);
        const settingsContent = settingsElements.create();
        this.docxContent.addXmlSettings(settingsContent);

        return this;
    }

    /**
     * Gets template symbols
     *
     * @returns Template symbols
     */
    public getTemplateSymbols(): [string, string] {
        return [this.templateSymbolStart, this.templateSymbolEnd];
    }

    /**
     * Gets template variables
     *
     * @returns Variables
     */
    public getTemplateVariables(): Map<string, string[]> {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        const variables = new Map<string, string[]>();
        Element.targetsInDocument.forEach(target => {
            const contents = this.docxContent.getContentPathsByType(target);
            const variablesTarget: string[] = [];

            if (contents != null) {
                contents.forEach(contentPath => {
                    const textContent =
                        this.docxContent.getTextContent(contentPath);
                    if (textContent != null) {
                        const variablesTargetContent =
                            this.docxContent.extractXmlVariables(
                                textContent,
                                this.templateSymbolStart,
                                this.templateSymbolEnd,
                                this.regExprVariableSymbols
                            );
                        variablesTarget.push(...variablesTargetContent);
                    }
                });
            }
            variables.set(target, variablesTarget);
        });

        return variables;
    }

    /**
     * Modifies section layout
     *
     * @param options - Section options
     * @returns this
     */
    public modifySectionLayout(options: Element.OptionsSectionLayout): this {
        this.docxContent.modifySections(options);

        return this;
    }

    /**
     * Opens a DOCX template
     *
     * @param template - DOCX template
     * @param options - Template options
     * @returns Promise CreateDocx
     * @throws Error reading DOCX contents
     */
    public async openTemplate(
        template: ArrayBuffer,
        options: Element.OptionsTemplate = {}
    ): Promise<this> {
        const zip = new Zip();
        try {
            await zip.open(template).then(docxTemplateStructure => {
                this.docxContent = docxTemplateStructure;
            });
            this.docxContent.initDocxStructure();
        } catch (err) {
            throw new Error((err as Error).message);
        }

        // apply default options
        options = { ...Element.defaultOptionsTemplate, ...options };

        if (options.importMSWordDefaultStyles) {
            this.docxContent.importMSWordDefaultStyles(
                generateDefaultDocxStructure()
            );
        }

        return this;
    }

    /**
     * Processes the template to repair all variables
     *
     * @returns this
     */
    public processTemplate(): this {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        return this;
    }

    /**
     * Removes image variables
     *
     * @param variables - Variables
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     * @param options - Remove variable image options
     * @returns this
     */
    public removeVariableImage(
        variables: string[],
        target: Element.Target = "document",
        options: Element.OptionsRemoveVariableImage = {}
    ): this {
        // apply default options
        options = { ...Element.defaultOptionsRemoveVariableImage, ...options };

        const contents = this.docxContent.getContentPathsByType(target);

        if (contents != null) {
            contents.forEach(contentPath => {
                let textContent = this.docxContent.getTextContent(contentPath);
                if (textContent != null) {
                    variables.forEach(variable => {
                        let xpathQuery;
                        if (options.type == "block") {
                            xpathQuery =
                                '//w:p[.//w:drawing[descendant::wp:docPr[@descr="' +
                                this.templateSymbolStart +
                                applyHtmlSpecialChars(variable) +
                                this.templateSymbolEnd +
                                '" or @title="' +
                                this.templateSymbolStart +
                                applyHtmlSpecialChars(variable) +
                                this.templateSymbolEnd +
                                '"]]]';
                        } else {
                            xpathQuery =
                                '//w:r[.//w:drawing[descendant::wp:docPr[@descr="' +
                                this.templateSymbolStart +
                                applyHtmlSpecialChars(variable) +
                                this.templateSymbolEnd +
                                '" or @title="' +
                                this.templateSymbolStart +
                                applyHtmlSpecialChars(variable) +
                                this.templateSymbolEnd +
                                '"]]]';
                        }
                        if (options.firstMatch) {
                            xpathQuery = "(" + xpathQuery + ")[1]";
                        }
                        if (textContent != null) {
                            textContent = this.docxContent.removeXmlElement(
                                textContent,
                                xpathQuery
                            );
                        }
                    });

                    this.docxContent.addTextContent(contentPath, textContent);
                }
            });
        }

        return this;
    }

    /**
     * Removes text variables
     *
     * @param variables - Variables
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     * @param options - Remove variable text options
     * @returns this
     */
    public removeVariableText(
        variables: string[],
        target: Element.Target = "document",
        options: Element.OptionsRemoveVariableText = {}
    ): this {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        // apply default options
        options = { ...Element.defaultOptionsRemoveVariableText, ...options };

        if (options.type == "inline") {
            // inline removal

            // transform array to Map
            const variablesMap = new Map(
                variables.map(variable => [variable, ""])
            );

            // replace variables with empty values
            this.replaceVariableText(variablesMap, target, options);
        } else {
            // block removal
            const contents = this.docxContent.getContentPathsByType(target);
            if (contents != null) {
                contents.forEach(contentPath => {
                    let textContent =
                        this.docxContent.getTextContent(contentPath);
                    if (textContent != null) {
                        variables.forEach(variable => {
                            let xpathQuery =
                                '//w:p[w:r/w:t[text()[contains(.,"' +
                                this.templateSymbolStart +
                                applyHtmlSpecialChars(variable) +
                                this.templateSymbolEnd +
                                '")]]]';
                            if (options.firstMatch) {
                                xpathQuery = "(" + xpathQuery + ")[1]";
                            }
                            if (textContent != null) {
                                textContent = this.docxContent.removeXmlElement(
                                    textContent,
                                    xpathQuery
                                );
                            }
                        });

                        this.docxContent.addTextContent(
                            contentPath,
                            textContent
                        );
                    }
                });
            }
        }

        return this;
    }

    /**
     * Replaces text variables with external files
     *
     * @param variables - Variables
     * @param options - Replace variable external file options
     * @returns this
     */
    public replaceVariableExternalFile(
        variables: Map<string, WordFragment>,
        options: Element.OptionsReplaceVariableExternalFile = {}
    ): this {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        // apply default options
        options = {
            ...Element.defaultOptionsReplaceVariableExternalFile,
            ...options,
        };

        const optionsReplaceWordFragment: Element.OptionsReplaceVariableWordFragment =
            {
                firstMatch: options.firstMatch,
                type: "block",
            };

        this.replaceVariableWordFragment(
            variables,
            "document",
            optionsReplaceWordFragment
        );

        return this;
    }

    /**
     * Replaces image variables
     *
     * @param variables - Variables
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     * @param options - Replace variable image options
     * @returns this
     */
    public replaceVariableImage(
        variables: Map<string, ArrayBuffer>,
        target: Element.Target = "document",
        options: Element.OptionsReplaceVariableImage = {}
    ): this {
        // apply default options
        options = { ...Element.defaultOptionsReplaceVariableImage, ...options };

        const contents = this.docxContent.getContentPathsByType(target);

        if (contents != null) {
            contents.forEach(contentPath => {
                let textContent = this.docxContent.getTextContent(contentPath);
                if (textContent != null) {
                    for (const [
                        variableName,
                        variableValue,
                    ] of variables.entries()) {
                        let xpathQuery =
                            '//wp:docPr[@descr="' +
                            this.templateSymbolStart +
                            applyHtmlSpecialChars(variableName) +
                            this.templateSymbolEnd +
                            '" or @title="' +
                            this.templateSymbolStart +
                            applyHtmlSpecialChars(variableName) +
                            this.templateSymbolEnd +
                            '"]';

                        if (options.firstMatch) {
                            xpathQuery = "(" + xpathQuery + ")[1]";
                        }

                        // generate an image element to get image options
                        const optionsImage = options as Element.OptionsImage;
                        optionsImage.image = variableValue;

                        // new imageId
                        const imageId = generateUniqid("image");
                        const imageElement = new Element.CreateImage(
                            optionsImage,
                            imageId,
                            0 // idDocPr is not used but required by the method
                        );
                        imageElement.create();

                        if (textContent != null) {
                            const replaceImage =
                                this.docxContent.replaceXmlImage(
                                    textContent,
                                    xpathQuery,
                                    imageElement,
                                    imageId,
                                    options
                                );
                            textContent = replaceImage[0];

                            // add image file only if the replacement has found some result
                            if (replaceImage[1] > 0) {
                                this.docxContent.addFileContent(
                                    "word/media/" +
                                        imageId +
                                        "." +
                                        imageElement.getRelationshipFileExtension(),
                                    variableValue
                                );
                                // add default type
                                this.insertDefault(
                                    imageElement.getRelationshipFileExtension(),
                                    imageElement.getMime()
                                );
                                // insert external relationships
                                this.insertExternalRelationships(
                                    imageElement.getExternalRelationships(),
                                    target
                                );
                            }
                        }
                    }

                    this.docxContent.addTextContent(contentPath, textContent);
                }
            });
        }

        return this;
    }

    /**
     * Replaces list variables
     *
     * @param variables - Variables and values
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     * @param options - Replace variable list options
     * @returns this
     */
    public replaceVariableList(
        variables: Map<string, Element.ListNested[]>,
        target: Element.Target = "document",
        options: Element.OptionsReplaceVariableList = {}
    ): this {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        // apply default options
        options = { ...Element.defaultOptionsReplaceVariableList, ...options };

        const contents = this.docxContent.getContentPathsByType(target);
        // WordFragments in the list
        let wordFragmentValues = new Map<string, WordFragment>();

        if (contents != null) {
            contents.forEach(contentPath => {
                let textContent = this.docxContent.getTextContent(contentPath);
                if (textContent != null) {
                    for (const [
                        variableName,
                        variableValue,
                    ] of variables.entries()) {
                        let xpathQuery =
                            '//w:p[w:r/w:t[text()[contains(.,"' +
                            this.templateSymbolStart +
                            applyHtmlSpecialChars(variableName) +
                            this.templateSymbolEnd +
                            '")]]]';
                        if (options.firstMatch) {
                            xpathQuery = "(" + xpathQuery + ")[1]";
                        }
                        if (textContent != null) {
                            const listContent = this.docxContent.replaceXmlList(
                                textContent,
                                this.templateSymbolStart +
                                    applyHtmlSpecialChars(variableName) +
                                    this.templateSymbolEnd,
                                xpathQuery,
                                variableValue,
                                options,
                                this.templateSymbolStart,
                                this.templateSymbolEnd
                            );
                            textContent = listContent[0];
                            wordFragmentValues = new Map([
                                ...wordFragmentValues,
                                ...listContent[1],
                            ]);
                        }
                    }

                    this.docxContent.addTextContent(contentPath, textContent);
                }
            });
        }

        // replace pending WordFragments
        if (wordFragmentValues.size > 0) {
            const optionsReplaceVariableWordFragment: Element.OptionsReplaceVariableWordFragment =
                {
                    type: "inline",
                };
            this.replaceVariableWordFragment(
                wordFragmentValues,
                target,
                optionsReplaceVariableWordFragment
            );
        }

        return this;
    }

    /**
     * Replaces table variables
     *
     * @param variables - Variables and values
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     * @param options - Replace variable table options
     * @returns this
     */
    public replaceVariableTable(
        variables: Map<string, string | WordFragment>[][],
        target: Element.Target = "document",
        options: Element.OptionsReplaceVariableTable = {}
    ): this {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        // apply default options
        options = { ...Element.defaultOptionsReplaceVariableTable, ...options };

        const contents = this.docxContent.getContentPathsByType(target);
        // WordFragments in the table
        let wordFragmentValues = new Map<string, WordFragment>();

        if (contents != null) {
            contents.forEach(contentPath => {
                let textContent = this.docxContent.getTextContent(contentPath);
                if (textContent != null) {
                    variables.forEach(variablesTable => {
                        // get placeholders from the first entry
                        const firstRow = variablesTable[0];
                        if (firstRow != null) {
                            let xpathQuery = "//w:tbl/w:tr[";
                            Array.from(firstRow.keys()).forEach(
                                (rowVariable, indexRowVariable) => {
                                    if (indexRowVariable == 0) {
                                        xpathQuery +=
                                            'contains(.,"' +
                                            this.templateSymbolStart +
                                            rowVariable +
                                            this.templateSymbolEnd +
                                            '")';
                                    } else {
                                        xpathQuery +=
                                            ' and contains(.,"' +
                                            this.templateSymbolStart +
                                            rowVariable +
                                            this.templateSymbolEnd +
                                            '")';
                                    }
                                }
                            );
                            xpathQuery += "]";
                            if (options.firstMatch) {
                                xpathQuery = "(" + xpathQuery + ")[1]";
                            }

                            if (textContent != null) {
                                const tableContent =
                                    this.docxContent.replaceXmlTable(
                                        textContent,
                                        variablesTable,
                                        xpathQuery,
                                        options,
                                        this.templateSymbolStart,
                                        this.templateSymbolEnd
                                    );
                                textContent = tableContent[0];

                                wordFragmentValues = new Map([
                                    ...wordFragmentValues,
                                    ...tableContent[1],
                                ]);
                            }
                        }
                    });

                    this.docxContent.addTextContent(contentPath, textContent);
                }
            });
        }

        // replace pending WordFragments
        if (wordFragmentValues.size > 0) {
            const optionsReplaceVariableWordFragment: Element.OptionsReplaceVariableWordFragment =
                {
                    type: "block",
                };
            this.replaceVariableWordFragment(
                wordFragmentValues,
                target,
                optionsReplaceVariableWordFragment
            );
        }

        return this;
    }

    /**
     * Replaces text variables with text contents
     *
     * @param variables - Variables and values
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     * @param options - Replace variable text options
     * @returns this
     */
    public replaceVariableText(
        variables: Map<string, string>,
        target: Element.Target = "document",
        options: Element.OptionsReplaceVariableText = {}
    ): this {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        // apply default options
        options = { ...Element.defaultOptionsReplaceVariableText, ...options };

        const contents = this.docxContent.getContentPathsByType(target);

        if (contents != null) {
            contents.forEach(contentPath => {
                let textContent = this.docxContent.getTextContent(contentPath);
                if (textContent != null) {
                    for (let [
                        variableName,
                        variableValue,
                    ] of variables.entries()) {
                        variableName = applyHtmlSpecialChars(variableName);
                        variableValue = applyHtmlSpecialChars(variableValue);

                        // replace line breaks
                        if (options.parseLineBreaks) {
                            variableValue = variableValue.replace(
                                /\n\r|\n|\r\n|\r/g,
                                '</w:t><w:br/><w:t xml:space="preserve">'
                            );
                        }

                        if (options.firstMatch != null && options.firstMatch) {
                            // replace only the first match
                            textContent = textContent.replace(
                                this.templateSymbolStart +
                                    variableName +
                                    this.templateSymbolEnd,
                                variableValue
                            );
                        } else {
                            textContent = textContent.replace(
                                generateRegExpForReplacement(
                                    this.templateSymbolStart +
                                        variableName +
                                        this.templateSymbolEnd
                                ),
                                variableValue
                            );
                        }
                    }

                    this.docxContent.addTextContent(contentPath, textContent);
                }
            });
        }

        return this;
    }

    /**
     * Replaces text variables with WordFragments
     *
     * @param variables - Variables and values
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     * @param options - Replace variable WordFragment options
     * @returns this
     */
    public replaceVariableWordFragment(
        variables: Map<string, WordFragment>,
        target: Element.Target = "document",
        options: Element.OptionsReplaceVariableWordFragment = {}
    ): this {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        // apply default options
        options = {
            ...Element.defaultOptionsReplaceVariableWordFragment,
            ...options,
        };

        const contents = this.docxContent.getContentPathsByType(target);

        if (contents != null) {
            contents.forEach(contentPath => {
                let textContent = this.docxContent.getTextContent(contentPath);
                if (textContent != null) {
                    for (const [
                        variableName,
                        variableValue,
                    ] of variables.entries()) {
                        let xpathQuery =
                            '//w:p[w:r/w:t[text()[contains(.,"' +
                            this.templateSymbolStart +
                            applyHtmlSpecialChars(variableName) +
                            this.templateSymbolEnd +
                            '")]]]';
                        if (options.firstMatch) {
                            xpathQuery = "(" + xpathQuery + ")[1]";
                        }
                        if (textContent != null) {
                            textContent =
                                this.docxContent.replaceXmlWordFragment(
                                    textContent,
                                    xpathQuery,
                                    this.templateSymbolStart +
                                        applyHtmlSpecialChars(variableName) +
                                        this.templateSymbolEnd,
                                    variableValue,
                                    options
                                );

                            this.insertExternalDependencies(
                                variableValue,
                                target
                            );
                        }
                    }

                    this.docxContent.addTextContent(contentPath, textContent);
                }
            });
        }

        return this;
    }

    /**
     * Replaces text variables with WordML
     *
     * @param variables - Variables
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     * @param options - Replace variable WordMl
     * @returns this
     */
    public replaceVariableWordMl(
        variables: Map<string, WordFragment>,
        target: Element.Target = "document",
        options: Element.OptionsReplaceVariableWordMl = {}
    ): this {
        if (!this.preprocessed) {
            this.repairVariables();
        }

        // apply default options
        options = {
            ...Element.defaultOptionsReplaceVariableWordMl,
            ...options,
        };

        const optionsReplaceWordFragment: Element.OptionsReplaceVariableWordFragment =
            {
                firstMatch: options.firstMatch,
                type: "block",
            };

        this.replaceVariableWordFragment(
            variables,
            target,
            optionsReplaceWordFragment
        );

        return this;
    }

    /**
     * Saves the DOCX
     *
     * @returns Promise ArrayBuffer
     */
    public async save(): Promise<ArrayBuffer> {
        const zip = new Zip();

        return zip.generateArrayBuffer(this.docxContent);
    }

    /**
     * Sets RTL and bidi
     *
     * @param rtl - RTL
     * @param bidi - Bidi
     * @returns this
     */
    public setRtl(rtl: boolean = true, bidi: boolean = true): this {
        this.rtl = rtl;
        this.bidi = bidi;

        return this;
    }

    /**
     * Sets template symbols
     *
     * @param templateSymbolStart - Template symbol start
     * @param templateSymbolEnd - Template symbol end
     * @returns this
     */
    public setTemplateSymbols(
        templateSymbolStart: string,
        templateSymbolEnd?: string
    ): this {
        this.templateSymbolStart = templateSymbolStart;
        // use the same symbol as templateSymbolStart if templateSymbolEnd is null
        this.templateSymbolEnd = templateSymbolEnd ?? templateSymbolStart;

        // remove spaces from template symbols
        this.templateSymbolStart = this.templateSymbolStart.replace(/\s+/g, "");
        this.templateSymbolEnd = this.templateSymbolEnd.replace(/\s+/g, "");

        // preprocessed is false because the template symbols have changed
        this.preprocessed = false;

        return this;
    }

    /**
     * Generates a paragraph
     *
     * @param content - Paragraph contents
     * @param options - Paragraph options
     * @returns Paragraph contents
     */
    protected generateParagraph(
        content: string,
        options: Element.OptionsParagraph = {}
    ): string {
        const paragraphElement = new Element.CreateParagraph(options);
        content = paragraphElement.create(content);

        return content;
    }

    /**
     * Inserts default
     *
     * @param extension - File extension
     * @param contentType - Content type
     */
    protected insertDefault(extension: string, contentType: string) {
        this.docxContent.addXmlDefault(extension, contentType);

        // keep external defaults to be added from WordFragments
        this.externalDefaults.set(extension, contentType);
    }

    /**
     * Inserts external dependencies
     *
     * @param element - Element content
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     */
    protected insertExternalDependencies(
        element: Element.CreateElement | WordFragment,
        target: Element.Target = "document"
    ) {
        // insert external relationships
        this.insertExternalRelationships(
            element.getExternalRelationships(),
            target
        );

        // insert defaults
        for (const [extension, contentType] of element
            .getExternalDefaults()
            .entries()) {
            this.insertDefault(extension, contentType);
        }

        // insert files
        const files =
            element instanceof Element.CreateElement
                ? element.getFileContents()
                : element.docxContent.getFileContents();
        for (const [path, file] of files.entries()) {
            this.docxContent.addFileContent(path, file);
        }
    }

    /**
     * Inserts external relationships
     *
     * @param externalRelationships - External relationships
     * @param target - Target: document (default), header, footer, footnote, endnote, comment
     */
    protected insertExternalRelationships(
        externalRelationships: Element.Relationship[],
        target: Element.Target = "document"
    ) {
        externalRelationships.forEach(externalRelationship => {
            this.docxContent.addXmlRelationship(externalRelationship, target);

            // keep external relationships to be added from WordFragments
            this.externalRelationships.push(externalRelationship);
        });
    }

    /**
     * Repairs variables in the template
     */
    protected repairVariables() {
        // iterate all supported targets to repair the variables

        Element.targetsInDocument.forEach(target => {
            const contents = this.docxContent.getContentPathsByType(target);

            if (contents != null) {
                contents.forEach(contentPath => {
                    let textContent =
                        this.docxContent.getTextContent(contentPath);
                    if (textContent != null) {
                        textContent = this.docxContent.repairXmlVariables(
                            textContent,
                            this.templateSymbolStart,
                            this.templateSymbolEnd
                        );

                        this.docxContent.addTextContent(
                            contentPath,
                            textContent
                        );
                    }
                });
            }
        });

        this.preprocessed = true;
    }

    /**
     * Sets RTL values
     *
     * @param options - Paragraph options
     * @returns Paragraph options
     */
    protected setRtlValues(
        options: Element.OptionsParagraph
    ): Element.OptionsParagraph {
        if (options.bidi == null && this.bidi) {
            options.bidi = this.bidi;
        }
        if (options.rtl == null && this.rtl) {
            options.rtl = this.rtl;
        }

        return options;
    }
}

export { CreateDocx };
